import React from 'react';
import { motion } from 'framer-motion';

// Image imports
const brewlogixApp = '/images/brewlogix-app.png';
const brewlogixAppSuite = '/images/brewlogix-app-suite.png';
const brewlogixDesignSystemFoundations = '/images/brewlogix-design-system-foundations.png';
const brewlogixDesignSystemComponents = '/images/brewlogix-design-system-components.png';
const brewlogixDesignSystemBdbMobileComponents = '/images/brewlogix-design-system-bdb-mobile-components.png';
const brewlogixDesignSystemBlxAppComponents = '/images/brewlogix-design-system-blx-app-components.png';
const brewlogixStyleGuide = '/images/brewlogix-style-guide.png';
const brewerydbInAppCta = '/images/brewerydb-in-app-cta.png';
const brewlogixOnDeckBefore = '/images/brewlogix-on-deck-before.png';
const brewlogixOnDeckAfter = '/images/brewlogix-on-deck-after.png';
const brewlogixEngineeringHandoffFigmaFileStructure = '/images/brewlogix-engineering-handoff-figma-file-structure.png';
const brewlogixOnDeck = '/images/brewlogix-on-deck.png';
const brewlogixOnTap = '/images/brewlogix-on-tap.png';
const brewlogixTeamManagementMembers = '/images/brewlogix-team-management-members.png';
const brewlogixTeamManagementInvite = '/images/brewlogix-team-management-invite.png';
const brewlogixBilling = '/images/brewlogix-billing.png';
const brewlogixColocationHome = '/images/brewlogix-colocation-home.png';

const BrewLogix: React.FC = () => {
  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="mx-auto max-w-8xl flex flex-1 flex-col gap-2 p-4 md:p-6">
        <section className="grid md:grid-cols-12 my-4 md:my-8">
          <div className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-6 md:space-y-12">
            <motion.h1
              className="md:!leading-tight text-3xl md:text-4xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: 0.1,
                ease: "easeOut",
              }}
            >
              BrewLogix unified four brewery apps and drove higher cross-platform adoption.
            </motion.h1>
            <motion.div
              className="grid md:grid-cols-12 md:gap-6 space-y-4 md:space-y-0 col-span-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
                ease: "easeOut",
              }}
            >
              <div className="md:col-span-5 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Team</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Worked closely with engineers and product managers.</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Tools</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Figma<span className="mx-2">·</span>Axure RP</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Date</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">2022 - 2024</p>
                </div>
              </div>
              <div className="md:col-span-7 space-y-4 md:space-y-8">
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Role & Contributions</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Product Designer<span className="mx-1"> · </span>User Research<span className="mx-1"> · </span>Information Architecture<span className="mx-1"> · </span>Prototyping</p>
                </div>
                <div className="space-y-1 md:space-y-3">
                  <p className="font-mono-recursive md:text-lg">Key Achievements</p>
                  <p className="md:text-lg dark:dark:text-zinc-400">Designed a unified design system across four brewery apps and used an AI-driven feedback loop to reshape on-deck keg management with auto-suggested tap line assignments. The work drove higher cross-platform adoption and stronger user engagement across the suite.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
        <section className="mt-8 md:mt-14">
          <motion.figure
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img className="overflow-hidden rounded-lg" src={brewlogixApp} alt="BrewLogix co-location app" />
            <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
              BrewLogix co-location app
            </figcaption>
          </motion.figure>

          <div className="grid md:grid-cols-12 my-12 md:my-28 space-y-12 md:space-y-28">
            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Project Overview</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">BrewLogix's beverage IoT platform empowers breweries to monitor, manage, and market their products in real-time. The platform encompesses four applications: <span className="font-medium">PerformancePlatform</span>, <span className="font-medium">BrewMenu</span>, <span className="font-medium">MarketMyBrewery</span>, and <span className="font-medium">BreweryDB</span>. Each serves a distinct purpose while providing a unqiue ecosystem of tools and features for brewpubs, tap houses and breweries.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg" src={brewlogixAppSuite} alt="BrewLogix suite of apps" />
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                BrewLogix suite of apps
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Goals and Objectives</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">Leading the UX and UI design for these applications, my primary objective was to develop a <span className="font-medium">dynamic design system</span> that could maintain visual consistency across platforms while accommodating each application's specific needs.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">This required creating a unified foundation for <span className="font-medium">colors</span>, <span className="font-medium">tokens</span>, and <span className="font-medium">iconography</span>, as well as a robust <span className="font-medium">component library</span> that could support varied functionalities across the suite. The goal was to ensure users could navigate seamlessly between applications while preserving the integrity of BrewLogix's brand identity.</p>
            </motion.div>

            <motion.div
              className="col-span-12 grid sm:grid-cols-12 gap-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="sm:col-span-6">
                <motion.figure
                  className="bg-gray-lightest overflow-hidden rounded-lg"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <img className="overflow-hidden rounded-lg" src={brewlogixDesignSystemFoundations} alt="Design system foundations" />
                </motion.figure>
              </div>
              <div className="sm:col-span-6">
                <motion.figure
                  className="bg-gray-lightest overflow-hidden rounded-lg"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <img className="overflow-hidden rounded-lg" src={brewlogixDesignSystemComponents} alt="Design system components" />
                </motion.figure>
              </div>
              <div className="sm:col-span-6">
                <motion.figure
                  className="bg-gray-lightest overflow-hidden rounded-lg"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <img className="overflow-hidden rounded-lg" src={brewlogixDesignSystemBdbMobileComponents} alt="BDB mobile components" />
                </motion.figure>
              </div>
              <div className="sm:col-span-6">
                <motion.figure
                  className="bg-gray-lightest overflow-hidden rounded-lg"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <img className="overflow-hidden rounded-lg" src={brewlogixDesignSystemBlxAppComponents} alt="BLX app components" />
                </motion.figure>
              </div>
            </motion.div>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Design System Strategy</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The design system was built from the ground up in Figma, beginning with <span className="font-medium">color and value tokens</span> that harmonized with the BrewLogix brand and met accessibility standards.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">From there, I established <span className="font-medium">design primitives</span> for typography, spacing, and elevation to provide a visually cohesive foundation. The <span className="font-medium">component library</span> included reusable assets ranging from standard interface elements to complex data visualizations, which were critical for creating unified yet customizable experiences across applications. <span className="font-medium">Custom icons</span> were designed to visually represent core actions like monitoring, receiving, and keg assignments, aiding intuitive navigation.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg" src={brewlogixStyleGuide} alt="BrewLogix style guide" />
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                BrewLogix style guide
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Challenges</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">A major challenge was delivering a unified experience across PerformancePlatform, MarketMyBrewery, and BrewMenu.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">Although each platform required distinct functionalities, there was a need for cross-platform compatibility to facilitate ease of use and to promote cross-selling. To address this, I established shared components that could be adapted as needed for each platform and created intuitive workflows that allowed users to navigate between features without losing context.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg" src={brewerydbInAppCta} alt="In-app CTAs" />
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                In-app CTAs
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Data-Driven Insights with AI-Driven Feedback Loop</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">One of the most impactful additions to the design was the feedback loop I created, which collected user feedback and behavior data, then used AI to analyze and extract actionable insights.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">For example, we observed that customers wanted to "lock lines," indicating a preference for assigning the same product to the same tap line with each new keg. Using these insights, we implemented an enhancement to the on-deck keg management system. This allowed for unlimited on-deck spots and introduced an auto-suggestion feature that recommends tap line assignments based on product history and attributes.</p>
            </motion.div>

            <motion.div
              className="col-span-12 grid sm:grid-cols-12 gap-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="sm:col-span-6">
                <motion.figure
                  className="bg-gray-lightest overflow-hidden rounded-lg"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <img className="overflow-hidden rounded-lg" src={brewlogixOnDeckBefore} alt="On-deck before" />
                </motion.figure>
              </div>
              <div className="sm:col-span-6">
                <motion.figure
                  className="bg-gray-lightest overflow-hidden rounded-lg"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <img className="overflow-hidden rounded-lg" src={brewlogixOnDeckAfter} alt="On-deck after" />
                </motion.figure>
              </div>
            </motion.div>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Design Operations and Handoff Solutions</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">To streamline design-to-engineering handoff, I developed a Figma file structure that enabled clear communication and ensured an efficient workflow.</p>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">This structure included a table of contents for improved navigation and organization. Additionally, each page in the file was named after its corresponding engineering ticket, containing all relevant designs, states, and annotations. A Master page maintained the latest version of each component, providing engineering with a single source of truth.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={brewlogixEngineeringHandoffFigmaFileStructure} alt="Figma file structure" />
              <figcaption className="text-center italic pt-2 text-gray-500 font-mono-recursive">
                Figma file structure
              </figcaption>
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Key Takeaways</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">This project demonstrated the value of a structured, data-driven approach to design. The well-crafted design system enabled seamless cross-platform navigation for users, while AI-driven analysis of user feedback facilitated the creation of impactful features such as the enhanced on-deck keg management. Thoughtful organization and documentation within the Figma file provided a streamlined handoff to engineering, fostering smooth collaboration and minimizing friction.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={brewlogixOnDeck} alt="On-deck management" />
            </motion.figure>

            <motion.div
              className="col-span-12 lg:col-start-3 lg:col-span-8 2xl:col-start-4 2xl:col-span-6 space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="text-xl md:text-2xl font-medium">Impact and Results</h2>
              <p className="lg:leading-normal text-md md:text-lg dark:text-zinc-400">The BrewLogix design system and UX solutions provided a unified, scalable framework for the platform's four applications, allowing the team to deliver cohesive, user-focused updates across all products. The enhanced user experience contributed to increased user engagement, higher cross-platform adoption, and positive feedback on the intuitive keg management and marketing tools.</p>
            </motion.div>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={brewlogixOnTap} alt="On-tap management" />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={brewlogixTeamManagementMembers} alt="Team management members" />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={brewlogixTeamManagementInvite} alt="Team management invite" />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={brewlogixBilling} alt="Billing management" />
            </motion.figure>

            <motion.figure
              className="col-span-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img className="overflow-hidden rounded-lg md:rounded-2xl" src={brewlogixColocationHome} alt="Colocation home" />
            </motion.figure>
          </div>
        </section>
      </div>

    </div>
  );
};

export default BrewLogix;
